import {
    useContext,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import type ILineChartTrendProps from "./type";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import Chart, {
    useChartValueTransition,
    getChartStaggerProgress,
    getChartSeriesColor,
    formatChartCompact,
    getChartCurvePath,
    getChartNiceScale,
    getChartTextWidth,
    getChartAreaPath,
    CHART_TICK_COUNT,
    useChartSeries,
    getChartXTicks,
    useChartModel
} from "../../../chart";
import stylesheet, {
    LINE_CHART_STACKED_FADE,
    LINE_CHART_OVERLAY_FADE,
    LINE_CHART_MIN_OPACITY,
    getLineChartMeasures,
    useStyles
} from "../../stylesheet";
import type {
    ChartTooltipContent,
    ChartColorIndex,
    ChartLegendItem,
    ChartPlotLayout,
    ChartTooltipRow,
    ChartDataRow,
    ChartPoint,
    ChartTick
} from "../../../chart/type";
import type {
    LineChartRenderLayer,
    LineChartLayerSeries,
    LineChartDataRow,
    LineChartShape
} from "../../type";
import {
    LinearGradient,
    Text as SvgText,
    ClipPath,
    Circle,
    Defs,
    Path,
    Line,
    Rect,
    Stop,
    G
} from "react-native-svg";
import {
    getLineChartColorParts,
    getLineChartGeometry,
    getLineChartXLabels,
    getLineChartMatrix,
    getLineChartExtent,
    getLineChartLabels,
    getLineChartPlot,
    getLineChartEnds
} from "../../util";
import {
    formatPercent,
    formatNumber
} from "../../../../helpers/locale";
import {
    CHART_PATTERN_ANGLES
} from "../../../chart/util";
import {
    ChartContext
} from "../../../chart/context";
import ChartPatternDefs from "../../../chart/components/pattern";

const LineChartTrend: FC<ILineChartTrendProps> = ({
    customLocalize: customLocalizeProp,
    valueFormatter: valueFormatterProp,
    customTheme: customThemeProp,
    endLabelBehaviour = "none",
    seriesKeys: seriesKeysProp,
    labelBehaviour = "none",
    plotSize = "medium",
    isShowDots = false,
    categoryFormatter,
    curve = "natural",
    fill = "gradient",
    isAnimatedOnMount,
    tooltipBehaviour,
    onActiveChange,
    stackBehaviour,
    categoryTitle,
    renderTooltip,
    categoryKey,
    isFilled,
    summary,
    style,
    data
}) => {
    const context = useContext(ChartContext);

    const customTheme = customThemeProp ?? context?.customTheme;

    const customLocalize = customLocalizeProp ?? context?.customLocalize;

    const {
        shapeConfigs,
        typography,
        durations,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const localID = useId().replace(/[^a-zA-Z0-9]/g, "");

    const baseID = `${context?.baseID ?? localID}-${isFilled ? "area" : "line"}`;

    const isStacked = stackBehaviour !== "overlay";

    const isExpanded = stackBehaviour === "expanded";

    const isFillVisible = isFilled && fill !== "none";

    const seriesKeys = seriesKeysProp ?? (context ? Object.keys(context.config) : Object.keys(data[0] ?? {}).filter((key) => key !== categoryKey));

    const seriesKeysKey = seriesKeys.join(",");

    const valueFormatter = valueFormatterProp ?? ((value: number) => formatNumber({
        options: {
            maximumFractionDigits: 2
        },
        locale: activeLocale,
        value
    }));

    const config = context?.config;

    const legendItems = useMemo((): Array<ChartLegendItem> => seriesKeys.map((key, index) => ({
        colorIndex: config?.[key]?.colorIndex ?? ((index % 8) + 1) as ChartColorIndex,
        shape: isFilled ? "square" : "line",
        title: config?.[key]?.title ?? key,
        key
    })), [
        seriesKeysKey,
        isFilled,
        config
    ]);

    const modelData = useMemo((): Array<ChartDataRow> => data.map((row: LineChartDataRow) => {
        const output: ChartDataRow = {};

        Object.keys(row).forEach((key) => {
            const value = row[key];

            if(value !== null && value !== undefined) {
                output[key] = value;
            }
        });

        return output;
    }), [
        data
    ]);

    useChartModel({
        model: {
            kind: isFilled ? "area" : "line",
            isShowTotal: isStacked,
            categoryFormatter,
            data: modelData,
            valueFormatter,
            categoryTitle,
            categoryKey,
            legendItems,
            seriesKeys,
            isStacked
        }
    });

    const {
        series
    } = useChartSeries({
        seriesKeys
    });

    const {
        valueLabel: valueLabelColor,
        nameLabel: nameLabelColor,
        crosshair: crosshairColor
    } = useStyles({
        colors
    });

    const measures = getLineChartMeasures({
        shapeConfigs,
        typography,
        borders,
        spaces
    });

    const count = data.length;

    const matrix = useMemo(() => getLineChartMatrix({
        seriesKeys,
        data
    }), [
        seriesKeysKey,
        data
    ]);

    const visibleIndexes = series.map((item, index) => item.isHidden ? -1 : index).filter((index) => index >= 0);

    const extent = getLineChartExtent({
        stackBehaviour,
        visibleIndexes,
        matrix,
        count
    });

    const scale = getChartNiceScale({
        count: CHART_TICK_COUNT,
        min: extent.min,
        max: extent.max
    });

    const getTickLabel = (value: number): string => isExpanded ? formatPercent({
        options: {
            maximumFractionDigits: 0
        },
        locale: activeLocale,
        value: value / 100
    }) : formatChartCompact(value, activeLocale);

    const tickLabels = scale.ticks.map((tick) => getTickLabel(tick));

    const categories = data.map((row, index) => {
        const value = row[categoryKey] ?? index;

        return categoryFormatter ? categoryFormatter(value, index) : String(value);
    });

    const {
        fontSize
    } = measures.typography;

    const yAxisWidth = Math.max(0, ...tickLabels.map((text) => getChartTextWidth({
        fontSize,
        text
    }))) + measures.spacingSm;

    const getEndText = (seriesIndex: number): string | undefined => {
        const index = count - 1;

        const value = matrix.values[seriesIndex * count + index] ?? 0;

        if(index < 0 || !matrix.mask[seriesIndex * count + index]) {
            return undefined;
        }

        return endLabelBehaviour === "leader" ? series[seriesIndex]?.title : valueFormatter(value);
    };

    const endTexts = endLabelBehaviour === "none" ? [] : visibleIndexes.map((seriesIndex) => getEndText(seriesIndex)).filter((text): text is string => text !== undefined);

    const endTextWidth = Math.max(0, ...endTexts.map((text) => getChartTextWidth({
        fontSize,
        text
    })));

    const dotReach = measures.dotRadius + measures.haloWidth;

    const endReserve = endLabelBehaviour === "none" || !endTexts.length ? dotReach : endLabelBehaviour === "label" ? dotReach + measures.spacingXs + endTextWidth : dotReach + measures.spacingSm * 2 + measures.spacingXs + endTextWidth;

    const maskKey = matrix.mask.map((item, index) => item ? -1 : index).filter((index) => index >= 0).join("-");

    const shapeKey = `${stackBehaviour}|${curve}|${seriesKeysKey}|${count}|${maskKey}`;

    const shape: LineChartShape = {
        mask: matrix.mask,
        stackBehaviour,
        key: shapeKey,
        seriesKeys,
        count,
        curve
    };

    const shapeRef = useRef<LineChartShape>(shape);

    const previousShapeRef = useRef<LineChartShape | undefined>(undefined);

    if(shapeRef.current.key !== shape.key) {
        previousShapeRef.current = shapeRef.current;
    }

    shapeRef.current = shape;

    const transition = useChartValueTransition({
        values: [
            ...matrix.values,
            scale.min,
            scale.max
        ],
        customTheme,
        shapeKey
    });

    const weights = series.map((item) => isStacked ? item.opacity : 1);

    const drawOrder = series.map((_, index) => index).sort((first, second) => Number(series[second]?.colorIndex === "muted") - Number(series[first]?.colorIndex === "muted"));

    const getPlot = (layout: Pick<ChartPlotLayout, "height" | "isRTL" | "width">) => getLineChartPlot({
        height: layout.height,
        isRTL: layout.isRTL,
        width: layout.width,
        yAxisWidth,
        endReserve,
        measures
    });

    const geometryCache: Record<string, ReturnType<typeof getLineChartGeometry>> = {};

    const getGeometry = (layout: Pick<ChartPlotLayout, "height" | "isRTL" | "width">) => {
        const key = `${layout.width}-${layout.height}-${layout.isRTL}`;

        const cached = geometryCache[key];

        if(cached) {
            return cached;
        }

        const geometry = getLineChartGeometry({
            values: transition.values,
            plot: getPlot(layout),
            isRTL: layout.isRTL,
            weights,
            shape
        });

        geometryCache[key] = geometry;

        return geometry;
    };

    const getCategoryText = (index: number): string => categories[index] ?? String(index);

    const getRowValue = (index: number, seriesIndex: number): string => {
        const rawIndex = seriesIndex * count + index;

        if(!matrix.mask[rawIndex]) {
            return "–";
        }

        const value = matrix.values[rawIndex] ?? 0;

        if(!isExpanded) {
            return valueFormatter(value);
        }

        const total = visibleIndexes.reduce((sum, itemIndex) => sum + (matrix.values[itemIndex * count + index] ?? 0), 0);

        return formatPercent({
            value: total ? value / total : 0,
            options: {
                maximumFractionDigits: 1
            },
            locale: activeLocale
        });
    };

    const getTooltipRows = (index: number): Array<ChartTooltipRow> => visibleIndexes.map((seriesIndex): ChartTooltipRow => {
        const item = series[seriesIndex] as LineChartLayerSeries & { title: string; colorIndex: ChartColorIndex };

        return {
            value: getRowValue(index, seriesIndex),
            shape: isFilled ? "square" : "line",
            colorIndex: item.colorIndex,
            title: item.title,
            key: item.key
        };
    });

    const getTooltip = (index: number): ChartTooltipContent => {
        const total = visibleIndexes.reduce((sum, seriesIndex) => sum + (matrix.values[seriesIndex * count + index] ?? 0), 0);

        return {
            sum: isStacked ? {
                title: localize("chart-total"),
                value: valueFormatter(total)
            } : undefined,
            title: getCategoryText(index),
            rows: getTooltipRows(index)
        };
    };

    const getReading = (index: number): string => {
        const rows = getTooltipRows(index).map((row) => `${row.title} ${row.value}`).join(", ");

        return `${getCategoryText(index)}: ${rows}`;
    };

    const getHighest = (): string => {
        const primary = categories.map((_, index) => {
            const items = visibleIndexes.map((seriesIndex) => matrix.values[seriesIndex * count + index] ?? 0);

            return isStacked ? items.reduce((sum, item) => sum + item, 0) : Math.max(0, ...items);
        });

        const highest = Math.max(...primary);

        const index = primary.indexOf(highest);

        if(index < 0 || isExpanded) {
            return "";
        }

        return localize("chart-highest-value", [
            getCategoryText(index),
            valueFormatter(highest)
        ]);
    };

    const currentSummary = summary ?? [
        context?.title,
        context?.description,
        getHighest()
    ].filter(Boolean).join(". ");

    const getIndex: Parameters<typeof Chart.Plot>[0]["getIndex"] = (point, layout) => {
        if(point.y < 0 || point.y > layout.height || count < 1) {
            return undefined;
        }

        const plot = getPlot(layout);

        const step = count > 1 ? plot.width / (count - 1) : plot.width;

        const tolerance = Math.min(step / 2, measures.spacingMd);

        if(point.x < plot.x - tolerance || point.x > plot.x + plot.width + tolerance) {
            return undefined;
        }

        if(count < 2 || !step) {
            return 0;
        }

        const offset = layout.isRTL ? plot.x + plot.width - point.x : point.x - plot.x;

        return Math.max(0, Math.min(count - 1, Math.round(offset / step)));
    };

    const getAnchor: Parameters<typeof Chart.Plot>[0]["getAnchor"] = (index, _seriesIndex, layout) => {
        const geometry = getGeometry(layout);

        const x = geometry.xs[index];

        const plot = getPlot(layout);

        if(x === undefined) {
            return undefined;
        }

        const tops = visibleIndexes.map((seriesIndex) => geometry.layers[seriesIndex]?.top[index]?.y).filter((value): value is number => value !== undefined);

        return {
            y: tops.length ? Math.min(...tops) : plot.y,
            x
        };
    };

    const renderGradients = (layer: LineChartRenderLayer) => {
        if(!isFillVisible || fill !== "gradient") {
            return null;
        }

        const overlayFade = isStacked || layer.items.length < 2 ? 1 : LINE_CHART_OVERLAY_FADE;

        return <Defs>
            {
                layer.items.map((item, index) => {
                    const parts = getLineChartColorParts(item.areaColor);

                    return <LinearGradient
                        id={`${layer.id}-${index}`}
                        key={item.key}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                    >
                        <Stop
                            stopOpacity={parts.opacity * overlayFade}
                            stopColor={parts.color}
                            offset="0"
                        />
                        <Stop
                            stopOpacity={parts.opacity * (isStacked ? LINE_CHART_STACKED_FADE : 0)}
                            stopColor={parts.color}
                            offset="1"
                        />
                    </LinearGradient>;
                })
            }
        </Defs>;
    };

    const renderFills = (layer: LineChartRenderLayer) => {
        if(!isFillVisible) {
            return null;
        }

        return <G
            opacity={layer.opacity}
            key={layer.id}
        >
            {renderGradients(layer)}
            {
                drawOrder.map((index) => {
                    const item = layer.items[index];

                    const geometryLayer = layer.geometry.layers[index];

                    if(!item || !geometryLayer || item.opacity < LINE_CHART_MIN_OPACITY) {
                        return null;
                    }

                    const areaPath = getChartAreaPath({
                        bottom: geometryLayer.bottom,
                        curve: layer.shape.curve,
                        top: geometryLayer.top
                    });

                    return <G
                        opacity={item.opacity}
                        key={item.key}
                    >
                        <Path
                            fill={fill === "gradient" ? `url(#${layer.id}-${index})` : item.areaColor}
                            d={areaPath}
                        />
                        {
                            CHART_PATTERN_ANGLES[item.pattern].map((angle) => <Path
                                fill={`url(#${baseID}-pattern-${angle})`}
                                d={areaPath}
                                key={angle}
                            />)
                        }
                    </G>;
                })
            }
        </G>;
    };

    const renderLines = (layer: LineChartRenderLayer) => {
        return <G
            opacity={layer.opacity}
            key={layer.id}
        >
            {
                drawOrder.map((index) => {
                    const item = layer.items[index];

                    const geometryLayer = layer.geometry.layers[index];

                    if(!item || !geometryLayer || item.opacity < LINE_CHART_MIN_OPACITY) {
                        return null;
                    }

                    return <Path
                        strokeWidth={measures.lineWidth}
                        d={getChartCurvePath({
                            curve: layer.shape.curve,
                            points: geometryLayer.top
                        })}
                        opacity={item.opacity}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        stroke={item.color}
                        key={item.key}
                        fill="none"
                    />;
                })
            }
        </G>;
    };

    const renderDot = ({
        surfaceColor,
        color,
        point
    }: {
        surfaceColor: string;
        point: ChartPoint;
        color: string;
    }) => {
        return <G
            key={`${point.x}-${point.y}-${color}`}
        >
            <Circle
                r={measures.dotRadius + measures.haloWidth}
                fill={surfaceColor}
                cx={point.x}
                cy={point.y}
            />
            <Circle
                r={measures.dotRadius}
                fill={color}
                cx={point.x}
                cy={point.y}
            />
        </G>;
    };

    const renderLabel = ({
        surfaceColor,
        isStrong,
        anchor,
        color,
        text,
        key,
        x,
        y
    }: {
        anchor: "start" | "middle" | "end";
        surfaceColor: string;
        isStrong: boolean;
        color: string;
        text: string;
        key: string;
        x: number;
        y: number;
    }) => {
        const fontProps = {
            fontWeight: isStrong ? measures.labelWeight : measures.typography.fontWeight,
            fontFamily: measures.typography.fontFamily,
            fontSize: measures.typography.fontSize,
            textAnchor: anchor,
            x,
            y
        };

        return <G
            aria-hidden={true}
            key={key}
        >
            <SvgText
                {...fontProps}
                strokeWidth={measures.haloWidth + measures.crosshairWidth}
                strokeLinejoin="round"
                stroke={surfaceColor}
                fill={surfaceColor}
            >
                {text}
            </SvgText>
            <SvgText
                {...fontProps}
                fill={color}
            >
                {text}
            </SvgText>
        </G>;
    };

    const renderMarks = ({
        geometry,
        layout,
        plot
    }: {
        geometry: ReturnType<typeof getLineChartGeometry>;
        plot: ReturnType<typeof getPlot>;
        layout: ChartPlotLayout;
    }) => {
        const activeIndex = layout.activeIndex;

        const hasActive = activeIndex !== undefined;

        const isShowEndDot = !hasActive && !isStacked && visibleIndexes.length === 1 && isFilled;

        const ends = getLineChartEnds({
            items: visibleIndexes.flatMap((seriesIndex) => {
                const item = series[seriesIndex];

                const point = geometry.layers[seriesIndex]?.top[count - 1];

                const text = getEndText(seriesIndex);

                return item && point && text !== undefined ? [
                    {
                        opacity: item.opacity,
                        color: item.color,
                        key: item.key,
                        point,
                        text
                    }
                ] : [];
            }),
            isRTL: layout.isRTL,
            endLabelBehaviour,
            measures,
            plot
        });

        const labels = labelBehaviour === "none" ? [] : getLineChartLabels({
            series: drawOrder.filter((seriesIndex) => !series[seriesIndex]?.isHidden).map((seriesIndex) => ({
                values: matrix.values.slice(seriesIndex * count, (seriesIndex + 1) * count),
                mask: matrix.mask.slice(seriesIndex * count, (seriesIndex + 1) * count),
                top: geometry.layers[seriesIndex]?.top ?? []
            })),
            isEndLabeled: endLabelBehaviour !== "none",
            isRTL: layout.isRTL,
            labelBehaviour,
            valueFormatter,
            measures,
            plot
        });

        return <>
            {
                ends.filter((end) => end.lead).map((end) => <Path
                    strokeWidth={measures.crosshairWidth}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    opacity={end.opacity}
                    stroke={end.color}
                    key={end.key}
                    d={end.lead}
                    fill="none"
                />)
            }
            {
                drawOrder.map((seriesIndex) => {
                    const item = series[seriesIndex];

                    const points = geometry.layers[seriesIndex]?.top ?? [];

                    if(!item || item.opacity < LINE_CHART_MIN_OPACITY) {
                        return null;
                    }

                    return <G
                        opacity={item.opacity}
                        key={item.key}
                    >
                        {
                            points.map((point, index) => {
                                const isEnd = index === count - 1 && (endLabelBehaviour !== "none" || isShowEndDot);

                                const isActive = index === activeIndex;

                                if(!point || (!isActive && !isShowDots && !isEnd)) {
                                    return null;
                                }

                                return renderDot({
                                    surfaceColor: layout.surfaceColor,
                                    color: item.color,
                                    point
                                });
                            })
                        }
                    </G>;
                })
            }
            {
                ends.map((end) => <G
                    key={`label-${end.key}`}
                    opacity={end.opacity}
                >
                    {renderLabel({
                        color: endLabelBehaviour === "leader" ? nameLabelColor : valueLabelColor,
                        isStrong: endLabelBehaviour === "label",
                        surfaceColor: layout.surfaceColor,
                        key: `${end.key}-text`,
                        anchor: end.anchor,
                        text: end.text,
                        x: end.x,
                        y: end.y
                    })}
                </G>)
            }
            {
                labels.map((label) => renderLabel({
                    key: `${label.x}-${label.y}-${label.text}`,
                    surfaceColor: layout.surfaceColor,
                    color: valueLabelColor,
                    anchor: label.anchor,
                    text: label.text,
                    isStrong: true,
                    x: label.x,
                    y: label.y
                }))
            }
        </>;
    };

    const renderPlot = (layout: ChartPlotLayout) => {
        const plot = getPlot(layout);

        const geometry = getGeometry(layout);

        const {
            isRTL
        } = layout;

        const progress = getChartStaggerProgress({
            progress: layout.entrance.progress,
            stagger: durations.stagger,
            duration: durations.long,
            intent: "chartDraw",
            index: 0,
            count: 1
        });

        const activeX = layout.activeIndex === undefined ? undefined : geometry.xs[layout.activeIndex];

        const pad = measures.dotRadius + measures.haloWidth;

        const revealFrom = isRTL ? plot.x + plot.width + pad : Math.max(0, plot.x - pad);

        const revealTo = isRTL ? 0 : layout.width;

        const revealSize = Math.abs(revealTo - revealFrom) * progress;

        const isRevealing = progress < 1;

        const gridPositions = scale.ticks.map((tick) => geometry.toValue(tick)).filter((position) => position >= plot.y - 0.5 && position <= plot.y + plot.height + 0.5);

        const valueTicks: Array<ChartTick> = scale.ticks.map((tick, index) => ({
            position: geometry.toValue(tick),
            label: tickLabels[index] ?? ""
        })).filter((tick) => tick.position >= plot.y - 0.5 && tick.position <= plot.y + plot.height + 0.5);

        const xLabels = getLineChartXLabels({
            categories,
            isRTL,
            plot
        });

        const labelWidth = Math.max(0, ...categories.map((text) => getChartTextWidth({
            fontSize,
            text
        })));

        const tickIndexes = getChartXTicks({
            minGap: measures.spacingSm + labelWidth / 2,
            width: plot.width,
            labelWidth,
            count
        });

        const previous = transition.previous;

        const previousShape = previousShapeRef.current;

        const hasPrevious = !!previous && !!previousShape && previous.values.length === previousShape.seriesKeys.length * previousShape.count + 2;

        const previousItems = previousShape ? previousShape.seriesKeys.map((key, index): LineChartLayerSeries => {
            const current = series.find((item) => item.key === key);

            const colorIndex = config?.[key]?.colorIndex ?? ((index % 8) + 1) as ChartColorIndex;

            const parts = current ?? {
                ...getChartSeriesColor({
                    colorIndex,
                    colors
                }),
                pattern: "none" as const,
                opacity: 1,
                key
            };

            return {
                areaColor: parts.areaColor,
                pattern: parts.pattern,
                opacity: parts.opacity,
                color: parts.color,
                key
            };
        }) : [];

        const currentLayer: LineChartRenderLayer = {
            opacity: transition.opacity,
            id: `${baseID}-fill`,
            items: series,
            geometry,
            shape
        };

        const previousLayer: LineChartRenderLayer | undefined = hasPrevious && previousShape && previous ? {
            geometry: getLineChartGeometry({
                weights: previousItems.map((item) => previousShape.stackBehaviour === "overlay" ? 1 : item.opacity),
                values: previous.values,
                shape: previousShape,
                isRTL,
                plot
            }),
            opacity: previous.opacity,
            id: `${baseID}-previous`,
            items: previousItems,
            shape: previousShape
        } : undefined;

        const layers = previousLayer ? [
            previousLayer,
            currentLayer
        ] : [
            currentLayer
        ];

        return <G>
            <Defs>
                <ClipPath
                    id={`${baseID}-reveal`}
                >
                    <Rect
                        x={isRTL ? revealFrom - revealSize : revealFrom}
                        height={layout.height}
                        width={revealSize}
                        y={0}
                    />
                </ClipPath>
            </Defs>
            {
                isFillVisible ?
                    <ChartPatternDefs
                        patterns={series.map((item) => item.pattern)}
                        customTheme={customTheme}
                        id={`${baseID}-pattern`}
                    />
                :
                    null
            }
            <Chart.Grid
                customTheme={customTheme}
                positions={gridPositions}
                plot={plot}
            />
            {
                activeX === undefined ?
                    null
                :
                    <Line
                        strokeWidth={measures.crosshairWidth}
                        y2={plot.y + plot.height}
                        stroke={crosshairColor}
                        x1={activeX}
                        x2={activeX}
                        y1={plot.y}
                    />
            }
            <G
                clipPath={isRevealing ? `url(#${baseID}-reveal)` : undefined}
            >
                {layers.map(renderFills)}
                {layers.map(renderLines)}
                <G
                    opacity={transition.opacity}
                >
                    {renderMarks({
                        geometry,
                        layout,
                        plot
                    })}
                </G>
            </G>
            <Chart.Axis
                customTheme={customTheme}
                orientation="vertical"
                ticks={valueTicks}
                isRTL={isRTL}
                tone="value"
                plot={plot}
            />
            <G
                aria-hidden={true}
            >
                {
                    tickIndexes.map((index) => {
                        const label = xLabels[index];

                        if(!label) {
                            return null;
                        }

                        return <SvgText
                            y={plot.y + plot.height + measures.spacingSm + fontSize}
                            fontWeight={measures.typography.fontWeight}
                            fontFamily={measures.typography.fontFamily}
                            key={`${index}-${label.text}`}
                            textAnchor={label.anchor}
                            fill={nameLabelColor}
                            fontSize={fontSize}
                            x={label.x}
                        >
                            {label.text}
                        </SvgText>;
                    })
                }
            </G>
        </G>;
    };

    return <Chart.Plot
        isAnimatedOnMount={isAnimatedOnMount}
        tooltipBehaviour={tooltipBehaviour}
        onActiveChange={onActiveChange}
        customLocalize={customLocalize}
        renderTooltip={renderTooltip}
        customTheme={customTheme}
        summary={currentSummary}
        style={[
            style,
            stylesheet.container
        ]}
        getTooltip={getTooltip}
        getReading={getReading}
        getAnchor={getAnchor}
        plotSize={plotSize}
        getIndex={getIndex}
        pointCount={count}
    >
        {renderPlot}
    </Chart.Plot>;
};
export default LineChartTrend;
